import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    CloseButton,
    Dialog,
    HStack,
    IconButton,
    Portal,
    Text,
    Textarea,
    VStack,
} from "@chakra-ui/react";
import { HiPaperClip, HiXMark } from "react-icons/hi2";

const TIMELINE_KINDS = ["PhoneCall", "Meeting", "Info", "Verification", "ManualEntry"];

const KIND_COLOR = {
    PhoneCall: "brand.400",
    Meeting: "purple.400",
    Info: "gray.400",
    Verification: "green.400",
    ManualEntry: "yellow.400",
};

const AddTimelineEntryModal = ({
    isOpen,
    onClose,
    kind,
    setKind,
    description,
    setDescription,
    occurredAt,
    setOccurredAt,
    files,
    setFiles,
    onSubmit,
    isLoading,
}) => {
    const { t } = useTranslation("models");

    useEffect(() => {
        if (isOpen) {
            setOccurredAt(new Date().toISOString().slice(0, 16));
        }
    }, [isOpen, setOccurredAt]);

    const kindLabel = (k) => {
        const map = {
            PhoneCall: t("candidateDetailPage.timelineKindPhoneCall"),
            Meeting: t("candidateDetailPage.timelineKindMeeting"),
            Info: t("candidateDetailPage.timelineKindInfo"),
            Verification: t("candidateDetailPage.timelineKindVerification"),
            ManualEntry: t("candidateDetailPage.timelineKindManual"),
        };
        return map[k] ?? k;
    };

    return (
        <Dialog.Root
            open={isOpen}
            onOpenChange={(e) => {
                if (!e.open) onClose();
            }}
        >
            <Portal>
                <Dialog.Backdrop />
                <Dialog.Positioner p={4}>
                    <Dialog.Content
                        bg="bg.card"
                        color="white"
                        borderColor="border.primary"
                        border="1px solid"
                        w={{ base: "calc(100vw - 2rem)", md: "lg" }}
                        maxW="100%"
                        maxH="calc(100dvh - 2rem)"
                        overflowY="auto"
                        borderRadius={12}
                    >
                        <Dialog.Body pt={6} pb={6} px={6}>
                            <Text fontWeight={700} fontSize="lg" mb={4}>
                                {t("candidateDetailPage.addTimelineEntryModalTitle")}
                            </Text>

                            <Text fontSize="sm" color="text.grey" mb={2}>
                                {t("candidateDetailPage.timelineKindSelectLabel")}
                            </Text>
                            <VStack align="stretch" gap={2} mb={4}>
                                {TIMELINE_KINDS.map((k) => (
                                    <Box
                                        key={k}
                                        as="button"
                                        onClick={() => setKind(k)}
                                        display="flex"
                                        alignItems="center"
                                        gap={3}
                                        px={3}
                                        py={2}
                                        borderRadius={8}
                                        border="1px solid"
                                        borderColor={kind === k ? "brand.400" : "border.primary"}
                                        bg={kind === k ? "rgba(26,165,255,0.08)" : "bg.secondary"}
                                        cursor="pointer"
                                        transition="all 0.15s"
                                        _hover={{ borderColor: "brand.400" }}
                                        outline="none"
                                        textAlign="left"
                                    >
                                        <Box
                                            w="3px"
                                            h="20px"
                                            borderRadius="full"
                                            bg={KIND_COLOR[k]}
                                            flexShrink={0}
                                        />
                                        <Text fontSize="sm">{kindLabel(k)}</Text>
                                    </Box>
                                ))}
                            </VStack>

                            <Text fontSize="sm" color="text.grey" mb={2}>
                                {t("candidateDetailPage.timelineEntryOccurredAtLabel")}
                            </Text>
                            <Box mb={4}>
                                <input
                                    type="datetime-local"
                                    value={occurredAt}
                                    onChange={(e) => setOccurredAt(e.target.value)}
                                    style={{
                                        width: "100%",
                                        background: "var(--chakra-colors-bg-secondary, #1a1d23)",
                                        border: "1px solid var(--chakra-colors-border-primary, #2d3139)",
                                        borderRadius: "8px",
                                        color: "white",
                                        padding: "8px 12px",
                                        fontSize: "14px",
                                        colorScheme: "dark",
                                    }}
                                />
                            </Box>

                            <Text fontSize="sm" color="text.grey" mb={2}>
                                {t("candidateDetailPage.timelineEntryDescriptionLabel")}
                            </Text>
                            <Textarea
                                placeholder={t("candidateDetailPage.timelineEntryDescriptionPlaceholder")}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={4}
                                bg="bg.secondary"
                                border="1px solid"
                                borderColor="border.primary"
                                color="white"
                                _placeholder={{ color: "text.grey" }}
                                resize="vertical"
                                mb={4}
                            />

                            <Box mb={4}>
                                <Text fontSize="sm" color="text.grey" mb={2}>
                                    {t("candidateDetailPage.timelineEntryAttachmentsLabel")}
                                </Text>
                                <Button
                                    as="label"
                                    htmlFor="timeline-file-input"
                                    variant="outlined"
                                    size="sm"
                                    cursor="pointer"
                                    mb={files.length > 0 ? 3 : 0}
                                >
                                    <HiPaperClip />
                                    {t("candidateDetailPage.timelineEntryAttachSelectFiles")}
                                    <input
                                        id="timeline-file-input"
                                        type="file"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={(e) => {
                                            const picked = Array.from(e.target.files ?? []);
                                            setFiles((prev) => [...prev, ...picked]);
                                            e.target.value = "";
                                        }}
                                    />
                                </Button>
                                {files.length > 0 && (
                                    <VStack align="stretch" gap={1}>
                                        {files.map((f, i) => (
                                            <HStack
                                                key={i}
                                                bg="bg.secondary"
                                                borderRadius={8}
                                                px={3}
                                                py={2}
                                                justify="space-between"
                                            >
                                                <HStack gap={2} minW={0}>
                                                    <Box color="text.grey" flexShrink={0}><HiPaperClip /></Box>
                                                    <Text fontSize="xs" color="white" fontFamily="mono" truncate>{f.name}</Text>
                                                </HStack>
                                                <IconButton
                                                    variant="ghost"
                                                    size="xs"
                                                    aria-label={t("candidateDetailPage.timelineEntryRemoveFile")}
                                                    color="text.grey"
                                                    flexShrink={0}
                                                    onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                                                >
                                                    <HiXMark />
                                                </IconButton>
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </Box>

                            <HStack justify="flex-end" gap={2}>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={onClose}
                                    disabled={isLoading}
                                >
                                    {t("candidateDetailPage.timelineEntryCancel")}
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    loading={isLoading}
                                    disabled={!kind || !description.trim()}
                                    onClick={onSubmit}
                                >
                                    {t("candidateDetailPage.timelineEntrySubmit")}
                                </Button>
                            </HStack>
                        </Dialog.Body>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" position="absolute" top="2" right="2" color="text.grey" />
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};

export default AddTimelineEntryModal;
